import { Box, Chip } from "@mui/material";
import { LinearTagChip } from "components/LinearTagChip";
import { LinearIssueStatusMap } from "hooks/useLinearIssueStatuses";
import React from "react";

// Generic row type that works for both state and local jurisdiction rows
type RowWithPilotFacilities = {
  pilotFacilities: Array<{
    workflowId?: string | null;
    workflowTags: Array<{ id: string; name: string }>;
  }>;
};

// Compute unique tags from all workflows in a row
const getRowTags = <T extends RowWithPilotFacilities>(row: T): string[] => {
  const allTags = new Set<string>();
  row.pilotFacilities.forEach((facility) => {
    facility.workflowTags?.forEach((tag) => {
      allTags.add(tag.name);
    });
  });
  return Array.from(allTags);
};

interface PilotWorkflowTagsCellProps<T extends RowWithPilotFacilities> {
  row: T;
  onOpenTagsDialog: (row: T) => void;
  linearStatusMap: LinearIssueStatusMap;
}

export const PilotWorkflowTagsCell = <T extends RowWithPilotFacilities>({
  row,
  onOpenTagsDialog,
  linearStatusMap,
}: PilotWorkflowTagsCellProps<T>) => {
  const stateTags = getRowTags(row);
  const hasWorkflows = row.pilotFacilities.some(
    (facility) => facility.workflowId !== null
  );
  const hasNoTags = stateTags.length === 0;

  // If there are tags, show them
  if (!hasNoTags) {
    // Show max 3-4 tags, then overflow if more
    const maxVisibleTags = 3;
    const hasOverflow = stateTags.length > maxVisibleTags;
    const visibleTags = hasOverflow
      ? stateTags.slice(0, maxVisibleTags)
      : stateTags;
    const overflowCount = stateTags.length - maxVisibleTags;

    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          overflow: "hidden",
          width: "100%",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onOpenTagsDialog(row);
        }}
      >
        {visibleTags.map((tag) => (
          <LinearTagChip
            key={tag}
            tag={tag}
            linearStatusMap={linearStatusMap}
            onNormalClick={() => onOpenTagsDialog(row)}
          />
        ))}
        {hasOverflow && (
          <Chip
            label={`+${overflowCount}`}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              flexShrink: 0,
              minWidth: "auto",
            }}
          />
        )}
      </Box>
    );
  }

  // If there are workflows but no tags, show "+ add tag" on hover
  if (hasWorkflows && hasNoTags) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          width: "100%",
          minHeight: 24,
        }}
      >
        <Chip
          label="+ add tag"
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onOpenTagsDialog(row);
          }}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            cursor: "pointer",
            opacity: 0,
            transition: "opacity 0.2s",
            ".MuiDataGrid-row:hover &": {
              opacity: 1,
            },
          }}
        />
      </Box>
    );
  }

  // No workflows, no tags - show nothing
  return null;
};
